import { useTranslation } from 'react-i18next';
import React, {useState, useEffect} from 'react';
import httpService from "../../../services/http.service";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import AppOpinion from "../../Shared/AppOpinion";
import AppPagination from "../../Shared/AppPagination";
import KpiCard from "../../Shared/KpiCard";
const queryString = require('query-string');

const FILTER_KEYS = ['keyword', 'sond_id', 'author', 'type', 'visibility', 'state', 'from', 'to', 'sort', 'boosted'];
const formatInt = (value) => (value === undefined || value === null ? '–' : Number(value).toLocaleString());

/** Médias de l'opinion au format attendu par AppOpinion : média principal (sond_picture) puis pièces jointes. */
const withAttachments = (obj) => {
    const attachments = [];
    if (obj?.sond_type && obj?.sond_picture) {
        attachments.push({attach_url: obj.sond_picture, attach_type: obj.sond_type, attach_thumbnail: obj.sond_thumbnail});
    }
    (obj?.attachments || []).forEach(attach => attachments.push({
        attach_url: attach.attach_url,
        attach_type: attach.attach_type,
        attach_thumbnail: attach.attach_thumbnail,
    }));
    return {...obj, attachments};
};

/**
 * Opinions publiées (/opinions) : carte de statistiques, onglets « Toutes / Boostées », filtres dans l'URL
 * (texte, auteur, type, visibilité, modération, période, tri), liste paginée.
 */
const OpinionList = ({history, location}) => {
    const { t } = useTranslation();
    const query = queryString.parse(location.search);
    const social = !!process.env.REACT_APP_SOCIAL_HUB;

    const [opinions, setOpinions] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [stats, setStats] = useState(null);
    const [meta, setMeta] = useState(null);
    const [form, setForm] = useState({keyword: query.keyword || '', author: query.author || ''});

    const setFilters = (changes) => {
        const next = {...query, ...changes};
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach(key => (next[key] === '' || next[key] === undefined || next[key] === null) && delete next[key]);
        history.push(`/opinions?${queryString.stringify(next)}`);
    };

    useEffect(() => {
        (async () => {
            try {
                const {data} = await httpService.get('/opinions/stats');
                setStats(data?.data || null);
            } catch (e) {
                setStats(null);
            }
        })();
    }, []);

    useEffect(() => {
        setForm({keyword: query.keyword || '', author: query.author || ''});
        setFetching(true);
        (async () => {
            try {
                const params = {page: query.page || 1};
                FILTER_KEYS.forEach(key => query[key] && (params[key] = query[key]));
                const {data: response} = await httpService.get('/opinions', {params});
                setOpinions((response.data || []).map(withAttachments));
                setMeta({
                    currentPage: response?.meta?.current_page,
                    lastPage: response?.meta?.last_page,
                    totalItems: response?.meta?.total,
                    hasNext: !!response?.links?.next,
                    hasPrevious: !!response?.links?.prev,
                });
            } catch (e) {
                setOpinions([]);
                setMeta(null);
            } finally {
                setFetching(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const onSearch = (e) => {
        e.preventDefault();
        setFilters({keyword: form.keyword.trim(), author: form.author.trim(), sond_id: ''});
    };

    const onPaginate = (direction) => {
        const current = parseInt(query.page || 1, 10);
        setFilters({page: direction === 'previous' ? current - 1 : current + 1});
    };

    // Boost ajouté ou retiré : l'opinion change d'onglet.
    const removeFromList = (opinion) => setOpinions(list => list.filter(obj => obj?.sond_id !== opinion?.sond_id));

    const hasFilters = FILTER_KEYS.some(key => key !== 'boosted' && key !== 'sort' && query[key]);
    const share = (value) => (stats && stats.total ? Math.round((value / stats.total) * 100) : 0);

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={social ? t("Posts publiés") : t("Opinions publiées")} />

            {/* Statistiques */}
            <div className="row clearfix equal-height-row">
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-comment-o" tone="blue" label={social ? t("Posts publiés") : t("Opinions publiées")} loading={!stats}
                             value={formatInt(stats?.total)} hint={t("{{n}} sur 7 jours · {{d}} aujourd'hui", {n: formatInt(stats?.last_7_days), d: formatInt(stats?.today)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-picture-o" tone="green" label={t("Avec médias")} loading={!stats}
                             value={formatInt(stats?.with_media)} progress={share(stats?.with_media)}
                             hint={t("{{p}} % du total · {{v}} vidéo(s)", {p: share(stats?.with_media), v: formatInt(stats?.videos)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-flag" tone="orange" label={t("Signalées")} loading={!stats}
                             value={formatInt(stats?.reported)} hint={t("{{m}} rendue(s) muette(s)", {m: formatInt(stats?.muted)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-rocket" tone="purple" label={t("Boostées en cours")} loading={!stats}
                             value={formatInt(stats?.boosted)} hint={t("{{n}} privée(s)", {n: formatInt(stats?.private)})} />
                </div>
            </div>

            {/* Onglets */}
            <ul className="nav stats-tabs" role="tablist">
                {[{value: '', label: t("Toutes")}, {value: '1', label: t("Boostées")}].map(tab => (
                    <li className="nav-item" key={tab.value || 'all'}>
                        <button type="button" role="tab" aria-selected={(query.boosted || '') === tab.value}
                                className={`nav-link btn btn-link ${(query.boosted || '') === tab.value ? 'active' : ''}`}
                                onClick={() => setFilters({boosted: tab.value})}>
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {/* Filtres */}
            <div className="card">
                <div className="body">
                    <form onSubmit={onSearch} className="row">
                        <div className="col-lg-4 col-md-6 mb-2">
                            <input className="form-control" placeholder={t("Mot clé dans le texte")} value={form.keyword}
                                   onChange={(e) => setForm({...form, keyword: e.target.value})} />
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <div className="input-group">
                                <div className="input-group-prepend"><span className="input-group-text">@</span></div>
                                <input className="form-control" placeholder={t("Auteur (nom d'utilisateur)")} value={form.author}
                                       onChange={(e) => setForm({...form, author: e.target.value})} />
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2 d-flex">
                            <input type="date" className="form-control mr-1" title={t("Du")} value={query.from || ''} onChange={(e) => setFilters({from: e.target.value})} />
                            <input type="date" className="form-control" title={t("Au")} value={query.to || ''} onChange={(e) => setFilters({to: e.target.value})} />
                        </div>
                        <div className="col-lg-2 col-md-6 mb-2">
                            <button type="submit" className="btn btn-primary btn-block"><i className="fa fa-search"></i>{' '}{t("Rechercher")}</button>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.type || ''} onChange={(e) => setFilters({type: e.target.value})}>
                                <option value="">{t("Tous les types")}</option>
                                <option value="text">{t("Texte seul")}</option>
                                <option value="media">{t("Avec médias")}</option>
                                <option value="image">{t("Images")}</option>
                                <option value="video">{t("Vidéos")}</option>
                                <option value="audio">{t("Audios")}</option>
                            </select>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.visibility || ''} onChange={(e) => setFilters({visibility: e.target.value})}>
                                <option value="">{t("Toutes les visibilités")}</option>
                                <option value="public">{t("Publiques")}</option>
                                <option value="private">{t("Privées")}</option>
                            </select>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.state || ''} onChange={(e) => setFilters({state: e.target.value})}>
                                <option value="">{t("Toutes (modération)")}</option>
                                <option value="reported">{t("Signalées")}</option>
                                <option value="muted">{t("Rendues muettes")}</option>
                            </select>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.sort || ''} onChange={(e) => setFilters({sort: e.target.value})}>
                                <option value="">{t("Plus récentes")}</option>
                                <option value="supports">{t("Plus soutenues")}</option>
                                <option value="comments">{t("Plus commentées")}</option>
                                <option value="reports">{t("Plus signalées")}</option>
                            </select>
                        </div>
                    </form>
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{meta ? t("{{n}} résultat(s)", {n: formatInt(meta.totalItems)}) : ''}</small>
                        {hasFilters && (
                            <button type="button" className="btn btn-link btn-sm p-0"
                                    onClick={() => history.push(`/opinions?${queryString.stringify(query.boosted ? {boosted: query.boosted} : {})}`)}>
                                <i className="fa fa-times"></i>{' '}{t("Effacer les filtres")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {fetching ? <FecthingSpinner /> : (
                <div className="row clearfix testimonials opinion-grid">
                    {opinions.length === 0 ? (
                        <div className="col-md-12">
                            <div className="card"><div className="body text-center text-muted py-5">{t("Aucune opinion ne correspond aux filtres.")}</div></div>
                        </div>
                    ) : opinions.map(opinion => (
                        // Deux opinions par ligne sur grand écran, une sur mobile ; cartes d'une même ligne à la même hauteur.
                        <div className="col-lg-6 col-md-12 d-flex opinion-grid__cell" key={opinion.sond_id}>
                            <AppOpinion
                                user={opinion?.user}
                                opinion={opinion}
                                truncateLength={350}
                                onRemoveBoost={() => removeFromList(opinion)}
                                onBoost={() => removeFromList(opinion)}
                            />
                        </div>
                    ))}
                    {meta && meta.lastPage > 1 && (
                        <div className="col-md-12">
                            <div className="card">
                                <div className="body">
                                    <AppPagination
                                        onNavigate={onPaginate}
                                        currentPage={meta.currentPage}
                                        hasNext={meta.hasNext}
                                        hasPrevious={meta.hasPrevious}
                                        totalItems={meta.totalItems}
                                        lastPage={meta.lastPage}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OpinionList;
